/**
 * reducers/users.ts
 * desc: user reducers
 * note: responsible for authentication and user-principle information
 */
import type { Reducer } from '@reduxjs/toolkit';
import constants from '../../settings/constants';
import type { UserRoles, UsersAction } from '../actions/users';
import type { UserDto, WhoAmIDto } from '../../types/api/users';
import type { RoleRepresentation } from '../../types/keycloak/roles';
import type { IdentityProviderRepresentation, UserRepresentation } from '../../types/keycloak/users';

// a Keycloak user as the list holds it: the display name is added on the way in, the role names once
// fetched, and the tier0-core user matched by email (null when there is none)
export type KeycloakUser = UserRepresentation & { dbUser?: UserDto | null; name?: string; roles?: string[] };

// the selected Keycloak user with the tier0-core user matched by email and its realm roles once fetched
export type KeycloakUserDetail = UserRepresentation & { dbUser?: UserDto; roles?: RoleRepresentation[] };

// the signed-in user; empty until /users/me answers
export type CurrentUser = Partial<WhoAmIDto>;

export interface UsersState {
    isLoading: {
        createUser: boolean;
        deleteUser: boolean;
        getUsersDb: boolean;
        getIdentityProviders: boolean;
        getUser: boolean;
        getUsers: boolean;
        getUsersRoles: boolean;
        updateUser: boolean;
        updateUserMe: boolean;
    };
    detail: KeycloakUserDetail;
    identityProviders: IdentityProviderRepresentation[];
    list: KeycloakUser[];
    listDb: UserDto[];
    me: CurrentUser;
}

const INITIAL_STATE: UsersState = {
    isLoading: {
        createUser: false,
        deleteUser: false,
        getUsersDb: false,
        getIdentityProviders: false,
        getUser: false,
        getUsers: false,
        getUsersRoles: false,
        updateUser: false,
        updateUserMe: false,
    },
    detail: {},
    identityProviders: [],
    list: [],
    listDb: [],
    me: {}
};


function usersReducer(state: UsersState = INITIAL_STATE, action: UsersAction): UsersState {
    switch (action.type) {
        // CLEAR Users
        case 'CLEAR_USERS':
            return _clearUsers(state);

        // CREATE User (Instance)
        case 'CREATE_USER_ERROR':
            return _createUserError(state);
        case 'CREATE_USER_REQUEST':
            return _createUserRequest(state);
        case 'CREATE_USER_SUCCESS':
            return _createUserSuccess(state);

        // CREATE User Roles (Instance)
        case 'CREATE_USER_ROLES_ERROR':
            return _updateUserError(state);
        case 'CREATE_USER_ROLES_REQUEST':
            return _updateUserRequest(state);
        case 'CREATE_USER_ROLES_SUCCESS':
            return _createUserRolesSuccess(state, action.payload);

        // DELETE User (Instance)
        case 'DELETE_USER_ERROR':
            return _deleteUserError(state);
        case 'DELETE_USER_REQUEST':
            return _deleteUserRequest(state);
        case 'DELETE_USER_SUCCESS':
            return _deleteUserSuccess(state, action.payload);

        // DELETE User Roles (Instance)
        case 'DELETE_USER_ROLES_ERROR':
            return _updateUserError(state);
        case 'DELETE_USER_ROLES_REQUEST':
            return _updateUserRequest(state);
        case 'DELETE_USER_ROLES_SUCCESS':
            return _deleteUserRolesSuccess(state, action.payload);

        // GET Identity Providers (List)
        case 'GET_IDENTITY_PROVIDERS_ERROR':
            return _getIdentityProvidersError(state);
        case 'GET_IDENTITY_PROVIDERS_REQUEST':
            return _getIdentityProvidersRequest(state);
        case 'GET_IDENTITY_PROVIDERS_SUCCESS':
            return _getIdentityProvidersSuccess(state, action.payload);

        // GET User (Instance)
        case 'GET_USER_ERROR':
            return _getUserError(state);
        case 'GET_USER_REQUEST':
            return _getUserRequest(state);
        case 'GET_USER_SUCCESS':
            return _getUserSuccess(state, action.payload, action.isKeyCloak);

        // GET Users DB (Collection)
        case 'GET_USERS_DB_ERROR':
            return _getUsersDbError(state);
        case 'GET_USERS_DB_REQUEST':
            return _getUsersDbRequest(state);
        case 'GET_USERS_DB_SUCCESS':
            return _getUsersDbSuccess(state, action.payload);

        // GET Users (Collection)
        case 'GET_USERS_ERROR':
            return _getUsersError(state);
        case 'GET_USERS_REQUEST':
            return _getUsersRequest(state);
        case 'GET_USERS_SUCCESS':
            return _getUsersSuccess(state, action.payload);
        
        // GET Users Roles (Collection)
        case 'GET_USERS_ROLES_ERROR':
            return _getUsersRolesError(state);
        case 'GET_USERS_ROLES_REQUEST':
            return _getUsersRolesRequest(state);
        case 'GET_USERS_ROLES_SUCCESS':
            return _getUsersRolesSuccess(state, action.payload);

        // Update User Me (Instance)
        case 'UPDATE_USER_ME_ERROR':
            return _updateUserMeError(state);
        case 'UPDATE_USER_ME_REQUEST':
            return _updateUserMeRequest(state);
        case 'UPDATE_USER_ME_SUCCESS':
            return _updateUserMeSuccess(state, action.payload);

        // Update User (Instance)
        case 'UPDATE_USER_ERROR':
            return _updateUserError(state);
        case 'UPDATE_USER_REQUEST':
            return _updateUserRequest(state);
        case 'UPDATE_USER_SUCCESS':
            return _updateUserSuccess(state, action.payload);

        // Update User Password (Instance)
        case 'UPDATE_USER_PASSWORD_ERROR':
            return _updateUserError(state);
        case 'UPDATE_USER_PASSWORD_REQUEST':
            return _updateUserRequest(state);
        case 'UPDATE_USER_PASSWORD_SUCCESS':
            return _updateUserPasswordSuccess(state);

        default:
            return state;
    }
}



//*************************************************
//*************   CLEAR Users   *******************
//*************************************************
function _clearUsers(state: UsersState): UsersState {
    return {
        ...state,
        isLoading: INITIAL_STATE.isLoading,
        detail: {},
        list: []
    };
}



//*************************************************
//*****   CREATE User (Instance)   ****************
//*************************************************
function _createUserError(state: UsersState): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createUser: false,
        },
    };
}

function _createUserRequest(state: UsersState): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createUser: true,
        },
    };
}

function _createUserSuccess(state: UsersState): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createUser: false,
        }
    };
}



//*************************************************
//*****   CREATE User Roles (Instance)   **********
//*************************************************
function _createUserRolesSuccess(state: UsersState, payload: RoleRepresentation[]): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateUser: false,
        },
        detail: {
            ...state.detail,
            roles: state.detail.roles ?
                [
                    ...state.detail.roles,
                    ...payload
                ]
                :
                [
                    ...payload
                ]
        }
    };
}



//*************************************************
//*****   DELETE User (Instance)   ****************
//*************************************************
function _deleteUserError(state: UsersState): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteUser: false,
        },
    };
}

function _deleteUserRequest(state: UsersState): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteUser: true,
        },
    };
}

function _deleteUserSuccess(state: UsersState, userId: string): UsersState {
    // create new list without deleted user
    const [ ...usersList ] = state.list;
    const deletedIndex = state.list.findIndex(x => x.id === userId);
    if (deletedIndex > -1) usersList.splice(deletedIndex, 1);

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteUser: false,
        },
        list: usersList,
    };
}



//*************************************************
//*****   DELETE User Roles (Instance)   **********
//*************************************************
function _deleteUserRolesSuccess(state: UsersState, payload: RoleRepresentation[]): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateUser: false,
        },
        detail: {
            ...state.detail,
            // the roles are fetched before the role editor shows; a delete without them throws, as it always has
            roles: state.detail.roles!.filter(role => !payload.some(item => item.id === role.id))
        }
    };
}



//*************************************************
//*****   GET Identity Providers (List)   *********
//*************************************************
function _getIdentityProvidersError(state: UsersState): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getIdentityProviders: false,
        },
    };
}

function _getIdentityProvidersRequest(state: UsersState): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getIdentityProviders: true,
        },
    };
}

function _getIdentityProvidersSuccess(state: UsersState, payload: IdentityProviderRepresentation[]): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getIdentityProviders: false,
        },
        identityProviders: payload
    };
}



//*************************************************
//*****   GET User (Instance)   *******************
//*************************************************
function _getUserError(state: UsersState): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getUser: false,
        },
    };
}

function _getUserRequest(state: UsersState): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getUser: true,
        },
    };
}

function _getUserSuccess(state: UsersState, payload: WhoAmIDto | KeycloakUserDetail, isKeyCloak: boolean): UsersState {
    // isKeyCloak tells the two payload shapes apart (see the action creator)
    const result: Partial<Pick<UsersState, 'detail' | 'me'>> = {};
    if (isKeyCloak) {
        result.detail = {
            ...state.detail,
            ...(payload as KeycloakUserDetail)
        };
    } else {
        result.me = _modifyUserDetail(payload as WhoAmIDto);
    }
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getUser: false,
        },
        ...result
    };
}



//*************************************************
//*****   GET Users DB (Collection)   *************
//*************************************************
function _getUsersDbError(state: UsersState): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getUsersDb: false,
        },
    };
}

function _getUsersDbRequest(state: UsersState): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getUsersDb: true,
        },
    };
}

function _getUsersDbSuccess(state: UsersState, payload: UserDto[]): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getUsersDb: false,
        },
        listDb: payload
    };
}



//*************************************************
//*****   GET Users (Collection)   ****************
//*************************************************
function _getUsersError(state: UsersState): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getUsers: false,
        },
    };
}

function _getUsersRequest(state: UsersState): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getUsers: true,
        },
    };
}

function _getUsersSuccess(state: UsersState, payload: KeycloakUser[]): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getUsers: false,
        },
        list: _modifyUsersList(payload),
    };
}



//*************************************************
//*****   GET Users Roles (Collection)   **********
//*************************************************
function _getUsersRolesError(state: UsersState): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getUsersRoles: false,
        },
    };
}

function _getUsersRolesRequest(state: UsersState): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getUsersRoles: true,
        },
    };
}

function _getUsersRolesSuccess(state: UsersState, payload: UserRoles[]): UsersState {
    const usersDetail = { ...state.detail };
    const usersList = [ ...state.list ];

    const foundSelectedUser = payload.find(item => item.userId === usersDetail.id );
    if (foundSelectedUser) {
        usersDetail.roles = _filterRoles(foundSelectedUser.roles);
    }

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getUsersRoles: false,
        },
        detail: usersDetail,
        list: usersList.map(user => {
            const newUser = { ...user };
            const foundRoles = payload.find(item => item.userId === user.id );
            if (foundRoles) {
                newUser.roles = _filterRoles(foundRoles.roles).map(item => item.name);
            }
            return newUser;
        }),
    };
}



//*************************************************
//*****   UPDATE User Me (Instance)   *************
//*************************************************
function _updateUserMeError(state: UsersState): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateUserMe: false,
        },
    };
}

function _updateUserMeRequest(state: UsersState): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateUserMe: true,
        },
    };
}

function _updateUserMeSuccess(state: UsersState, payload: UserDto): UsersState {
    const settled = _updateUserMeError(state);
    if (!payload?.id || payload.id !== state.me?.userDto?.id) return settled;
    return {
        ...settled,
        me: {
            ...state.me,
            userDto: payload,
        },
    };
}



//*************************************************
//*****   UPDATE User (Instance)   ****************
//*************************************************
function _updateUserError(state: UsersState): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateUser: false,
        },
    };
}

function _updateUserRequest(state: UsersState): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateUser: true,
        },
    };
}

function _updateUserSuccess(state: UsersState, payload: Partial<UserRepresentation>): UsersState {
    const usersList = [ ...state.list ];

    // if list exists, modify w updated user
    // note: list will not exist if deeplinked or refreshed detail page
    if (usersList.length) {
        const updatedIndex = usersList.findIndex(x => x.id === payload.id);
        if (updatedIndex > -1) {
            usersList[updatedIndex] =  {
                ...usersList[updatedIndex],
                ...payload
            };
        }
    }

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateUser: false,
        },
        detail: {
            ...state.detail,
            ...payload,
        },
        list: _modifyUsersList(usersList)
    };
}



//*************************************************
//*****   UPDATE User Password (Instance)   *******
//*************************************************
function _updateUserPasswordSuccess(state: UsersState): UsersState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateUser: false,
        }
    };
}



//*************************************************
//*****   Helper Functions   **********************
//*************************************************
/**
 * _modifyUserDetail
 * desc: modify user detail before putting into the store
 * note: the initial intention of this function is to remove redundant permissions that automatically created by keycloak and has implication in our system (YM-901).
 * @param  {object}  payload  Original payload
 * @return {object}           New payload
 */
const _modifyUserDetail = (payload: WhoAmIDto): WhoAmIDto => {
    const redundantPermissions = new Set([ 'ROLE_UMA_AUTHORIZATION', 'ROLE_OFFLINE_ACCESS' ]);

    if (payload.permissions) {
        payload.permissions = payload.permissions.filter((item) => !redundantPermissions.has(item));
    }

    return payload;
};

/**
 * _modifyUsersList
 * desc: modify users list before putting into the store
 * note: add additional info for users object.
 * @param  {array}  payload   Original payload
 * @return {array}            New payload
 */
const _modifyUsersList = (payload: KeycloakUser[]): KeycloakUser[] => {
    return payload.map(item => ({ ...item, name: item.firstName || item.lastName ? `${item.firstName || ''} ${item.lastName || ''}` : item.username }));
};

/**
 * _filterRoles
 * desc: modify roles list before putting into the store
 * note: remove redundant roles.
 * @param  {array}  roles     Original payload
 * @return {array}            New payload
 */
const _filterRoles = (roles: RoleRepresentation[]): RoleRepresentation[] => {
    return roles.filter(role => !(constants.HIDDEN_PERMISSIONS as readonly string[]).includes(role.name));
};

export default usersReducer as Reducer<UsersState>;
